import { useMemo, useRef, useState } from 'react';
import {
  Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { CAPABILITY_SCOPES } from '@/auth/scopes';
import { useResults } from '@/store/results';
import type { SiteInfo } from '@/graph/modules/sites';
import { getSiteLabelCoverage, type SiteLabelCoverage } from '@/graph/modules/labels';
import {
  aggregateLabelCoverage, overallCoverageForLabel, siteCoveragePct, type LabelCoverageResult,
} from './labelCoverage';
import { Panel } from '@/components/Panel';
import { ModuleGate } from '@/components/ModuleGate';
import { SitePicker } from '@/components/SitePicker';
import { Metric, MetricGrid } from '@/components/Metric';
import { ProgressBar } from '@/components/ProgressBar';
import { ErrorNotice } from '@/components/ErrorNotice';
import { IconLock } from '@/components/icons';
import { readToken } from '@/lib/theme';

type Status = 'idle' | 'scanning' | 'done' | 'error';

/**
 * C12 — Sensitivity-label coverage. For each SharePoint site, the share of files
 * carrying a sensitivity label. A label filter re-computes coverage for one chosen
 * label without re-scanning. Progressive, bounded scan; read-only.
 */
export function LabelCoveragePanel() {
  const setLabelCoverage = useResults((s) => s.setLabelCoverage);
  const [status, setStatus] = useState<Status>('idle');
  const [sites, setSites] = useState<SiteInfo[]>([]);
  const [maxFiles, setMaxFiles] = useState('200');
  const [done, setDone] = useState(0);
  const [total, setTotal] = useState(0);
  const [result, setResult] = useState<LabelCoverageResult | null>(null);
  const [labelFilter, setLabelFilter] = useState<string>(''); // '' = any label
  const [error, setError] = useState<unknown>(null);
  const cancelRef = useRef(false);

  const green = readToken('--green', '#3F7057');
  const orange = readToken('--orange', '#A97F3C');
  const red = readToken('--red', '#9E4F4F');
  const grid = readToken('--border', '#D4DDE2');
  const text3 = readToken('--text-3', '#6E7478');

  const filter = labelFilter || null;

  const chartData = useMemo(() => {
    if (!result) return [];
    return result.perSite.slice(0, 12).map((s) => ({
      name: s.displayName.length > 22 ? s.displayName.slice(0, 21) + '…' : s.displayName,
      pct: siteCoveragePct(s, filter),
    }));
  }, [result, filter]);

  async function run() {
    cancelRef.current = false;
    setStatus('scanning');
    setResult(null);
    setError(null);
    setDone(0);
    setTotal(0);
    const fileLimit = Math.max(1, Math.min(2000, Number(maxFiles) || 200));
    try {
      setTotal(sites.length);
      const perSite: SiteLabelCoverage[] = [];
      for (const site of sites) {
        if (cancelRef.current) break;
        perSite.push(await getSiteLabelCoverage(site, fileLimit));
        setDone(perSite.length);
      }
      const agg = aggregateLabelCoverage(perSite);
      setResult(agg);
      setLabelCoverage(agg);
      setStatus('done');
    } catch (e) {
      setError(e);
      setStatus('error');
    }
  }

  const overall = result ? overallCoverageForLabel(result, filter) : 0;

  return (
    <Panel id="labels" icon={<IconLock />} title="Sensitivity-label coverage" cap="C12"
      why={CAPABILITY_SCOPES.C12.why}>
      <ModuleGate cap="C12">
        <h3 className="metric__label">Choose which sites to scan</h3>
        <SitePicker selected={sites} onChange={setSites} disabled={status === 'scanning'} />

        <div className="field" style={{ maxWidth: 180 }}>
          <label htmlFor="lc-files">Max files / site</label>
          <input id="lc-files" inputMode="numeric" value={maxFiles} disabled={status === 'scanning'}
            onChange={(e) => setMaxFiles(e.target.value.replace(/[^0-9]/g, ''))} />
        </div>

        <div className="gate__cta" style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn--primary" onClick={() => void run()}
            disabled={status === 'scanning' || sites.length === 0}>
            {status === 'scanning'
              ? 'Scanning…'
              : result
                ? 'Re-scan'
                : `Scan ${sites.length} selected site${sites.length === 1 ? '' : 's'}`}
          </button>
          {status === 'scanning' && <button className="btn btn--ghost" onClick={() => { cancelRef.current = true; }}>Stop</button>}
        </div>
        {sites.length === 0 && status !== 'scanning' && (
          <p className="small muted" style={{ marginTop: 8 }}>Search and select at least one site to scan.</p>
        )}

        {status === 'scanning' && (
          <div style={{ marginTop: 16 }}>
            <ProgressBar value={done} max={total} label="Scanning site libraries" />
          </div>
        )}

        {status === 'error' && <ErrorNotice error={error} />}

        {result && (
          <>
            {result.labels.length > 0 && (
              <div className="field" style={{ marginTop: 14 }}>
                <label htmlFor="lc-filter">Filter by label</label>
                <select id="lc-filter" className="select" value={labelFilter}
                  onChange={(e) => setLabelFilter(e.target.value)}>
                  <option value="">Any label</option>
                  {result.labels.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
            )}

            <MetricGrid>
              <Metric label="Sites scanned" value={result.sitesScanned} />
              <Metric label="Files scanned" value={result.totalFiles} />
              <Metric label={filter ? `“${filter}” coverage` : 'Labelled coverage'} value={`${overall}%`}
                tone={overall >= 80 ? 'green' : overall >= 50 ? 'accent' : 'red'} sub="Files carrying a label" />
              <Metric label="Distinct labels" value={result.labels.length}
                tone={result.labels.length === 0 ? 'red' : 'default'} sub="Seen in scan" />
            </MetricGrid>

            {result.totalFiles === 0 ? (
              <div className="notice notice--info">
                <strong>No files scanned</strong>
                <p>No readable document-library files were returned for the scanned sites, or the sensitivity-label
                  facet is not exposed via delegated Graph on this tenant. Label detection is best-effort here.</p>
              </div>
            ) : (
              <>
                <h3 className="metric__label" style={{ marginTop: 8 }}>
                  Coverage by site {filter ? `— label “${filter}”` : '— any label'} (lowest first)
                </h3>
                <div className="chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} layout="vertical" margin={{ top: 4, right: 12, bottom: 4, left: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={grid} horizontal={false} />
                      <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 12, fill: text3 }}
                        tickLine={false} axisLine={{ stroke: grid }} />
                      <YAxis type="category" dataKey="name" width={150}
                        tick={{ fontSize: 11, fill: text3 }} tickLine={false} axisLine={false} />
                      <Tooltip cursor={{ fill: readToken('--bg-2', '#EFF3F6') }}
                        contentStyle={{ borderRadius: 10, border: `1px solid ${grid}`, fontFamily: 'var(--font-mono)', fontSize: 12 }} />
                      <Bar dataKey="pct" name="Coverage %" radius={[0, 4, 4, 0]}>
                        {chartData.map((d, i) => (
                          <Cell key={i} fill={d.pct >= 80 ? green : d.pct >= 50 ? orange : red} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <table className="matrix">
                  <thead>
                    <tr><th>Site</th><th>Files</th><th>Labelled</th><th>{filter ? `“${filter}”` : 'Coverage'}</th></tr>
                  </thead>
                  <tbody>
                    {result.perSite.slice(0, 100).map((s) => {
                      const p = siteCoveragePct(s, filter);
                      return (
                        <tr key={s.siteId}>
                          <td className="matrix__label">{s.displayName}</td>
                          <td>{s.scanned}</td>
                          <td>{filter ? s.byLabel[filter] ?? 0 : s.labeled}</td>
                          <td>
                            <span className={`chip ${p >= 80 ? 'chip--pass' : p >= 50 ? 'chip--warn' : 'chip--critical'}`}>{p}%</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </>
            )}
            <p className="small muted" style={{ marginTop: 12 }}>
              Label detection is best-effort under delegated Graph (the driveItem sensitivity-label facet is read where
              the tenant exposes it). Generated client-side; nothing leaves your browser.
            </p>
          </>
        )}
      </ModuleGate>
    </Panel>
  );
}
